import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { roleOf } from "@/lib/statusRoles";
import { encodeForApi } from "@/lib/formEncode";
import { ACCEPT_ATTR, isAcceptedFile, isImageRef, uploadForEditor } from "@/lib/fileEmbed";
import { RemoveBadge } from "@/components/ui/RemoveBadge";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import type { BoardDetail, BoardKind, CellValue, ColumnDef, FileRef, UUID } from "@/lib/types";

// The WYSIWYG editor pulls in TipTap (~0.5 MB) — lazy so the form bundle only
// loads it when a long-text field renders. Paste/drop/pick of screenshots +
// files routes through onUploadFile (embeds them in the description markdown).
const LazyRichTextEditor = lazy(() =>
  import("@/components/ui/RichTextEditor").then((m) => ({ default: m.RichTextEditor })),
);

// Long-text form field = the WYSIWYG editor (prose only) + a staged-attachment
// strip. Screenshots/files added by paste, drag-drop OR the picker upload
// immediately and show as THUMBNAILS — never as raw ![](url) markdown in the
// editor. Mirrors the proven comment-composer pattern.
//
// WHERE THE FILES END UP depends on whether the board has a `file` column:
//
//   onAttachments PRESENT (board has a Datei column) — the staged refs are
//     reported upward and written to that column. The prose stays pure, and the
//     editor switches to onUploadFile so a screenshot pasted INTO THE BODY still
//     embeds inline. Routing follows the GESTURE, not the file type: the button
//     means "attach", pasting into the text means "show it here".
//
//   onAttachments ABSENT (no Datei column, e.g. the IT ticket board) — exactly
//     the old behaviour: every attachment is appended to the prose as markdown.
//     This is what keeps existing boards working with no migration.
// Only react to file drags — dragging selected text over the field must not
// trigger the drop affordance.
function dragHasFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types ?? []).includes("Files");
}

function LongTextField({
  value,
  onChange,
  placeholder,
  onAttachments,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onAttachments?: (refs: FileRef[]) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // The editor owns the prose; attachments are tracked separately. Initialised
  // once from `value` (the creation form starts empty); this component is then
  // the source of truth and reports the combined markdown upward.
  const [prose, setProse] = useState(value);
  const [attachments, setAttachments] = useState<FileRef[]>([]);
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire on every child too; a depth counter keeps the
  // highlight steady instead of flickering as the cursor crosses the editor.
  const dragDepth = useRef(0);
  const toColumn = Boolean(onAttachments);

  // Report upward whenever either changes. Both callbacks are read through refs
  // so this doesn't re-fire on every parent render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onAttachmentsRef = useRef(onAttachments);
  onAttachmentsRef.current = onAttachments;
  useEffect(() => {
    if (onAttachmentsRef.current) {
      onChangeRef.current(prose);
      onAttachmentsRef.current(attachments);
      return;
    }
    const embeds = attachments.map((a) =>
      isImageRef(a) ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`,
    );
    onChangeRef.current([prose, ...embeds].filter(Boolean).join("\n\n"));
  }, [prose, attachments]);

  async function attach(files: File[]) {
    const accepted = files.filter(isAcceptedFile);
    if (accepted.length === 0) return;
    setUploading((n) => n + accepted.length);
    for (const file of accepted) {
      try {
        // The full FileRef, not uploadForEditor's {name,url,isImage} reshape —
        // a file column cell stores the whole ref.
        const ref = await api.uploadFile(file);
        setAttachments((prev) => [...prev, ref]);
      } catch {
        showToast(t.commentFileFailed, "error");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }

  function handleDragEnter(e: React.DragEvent) {
    if (!dragHasFiles(e)) return;
    dragDepth.current += 1;
    setDragging(true);
  }
  function handleDragOver(e: React.DragEvent) {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function handleDragLeave(e: React.DragEvent) {
    if (!dragHasFiles(e)) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  }
  function handleDrop(e: React.DragEvent) {
    dragDepth.current = 0;
    setDragging(false);
    // The editor stages its own paste/drop via onFiles; the surrounding area is
    // handled here.
    if ((e.target as HTMLElement).closest(".flow-rte-content")) return;
    e.preventDefault();
    void attach(Array.from(e.dataTransfer.files));
  }

  const fallback = (
    <textarea
      value={prose}
      onChange={(e) => setProse(e.target.value)}
      rows={4}
      style={{ ...inputStyle, height: "auto", padding: "10px 12px", lineHeight: 1.45 }}
    />
  );

  return (
    <div
      style={{ position: "relative" }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragging ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            borderRadius: 8,
            border: `1.5px dashed ${BRAND.blue}`,
            background: "rgba(0, 89, 155, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            fontSize: 13,
            fontWeight: 600,
            color: BRAND.blue,
          }}
        >
          {t.formDropHint}
        </div>
      ) : null}
      <div
        style={{
          ...inputStyle,
          height: "auto",
          padding: "6px 10px",
          transition: "border-color .15s",
          ...(dragging ? { borderColor: BRAND.blue } : {}),
        }}
      >
        <Suspense fallback={fallback}>
          <LazyRichTextEditor
            value={prose}
            onChange={setProse}
            // Gesture split: with a Datei column, a paste/drop INSIDE the body
            // embeds inline (onUploadFile inserts at the cursor) and only the
            // button/outer-dropzone stages to the column. Without one, every
            // route stages, exactly as before.
            {...(toColumn
              ? { onUploadFile: uploadForEditor }
              : { onFiles: (files: File[]) => void attach(files) })}
            placeholder={placeholder}
          />
        </Suspense>
      </div>

      {attachments.length > 0 || uploading > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          {attachments.map((a, i) => (
            <div key={`${a.url}-${i}`} style={{ position: "relative" }}>
              {isImageRef(a) ? (
                <img
                  src={a.url}
                  alt={a.name}
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid var(--hairline)",
                  }}
                />
              ) : (
                <div
                  title={a.name}
                  style={{
                    maxWidth: 160,
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid var(--hairline)",
                    fontSize: 12,
                    color: "var(--ink-2)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {a.name}
                </div>
              )}
              <RemoveBadge
                label={t.formAttachRemove}
                onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
              />
            </div>
          ))}
          {uploading > 0 ? (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 8,
                border: "1px dashed var(--hairline)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: "var(--ink-3)",
              }}
            >
              …
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading > 0}
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: "var(--ink-2)",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 7,
            padding: "5px 10px",
            cursor: uploading > 0 ? "wait" : "pointer",
          }}
        >
          {uploading > 0 ? t.formAttachUploading : t.formAttachButton}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          void attach(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
    </div>
  );
}

// Per the form-UX spec: these column types are hidden from the public form.
// Person columns are an exception when named "Reporter" / "Gemeldet von" — we
// silently fill them with the current user. Status columns are handled
// specially (see visibleFormColumns): the WORKFLOW status (role-bearing) is
// hidden + auto-defaulted to "Offen" by the backend, while a non-workflow
// required single-select (e.g. Bereich) IS shown so the requester categorizes.
// The read-only meta columns (creator / creator_department / creator_company)
// are computed + snapshotted server-side at creation, so they are never input
// fields — they render filled on the created ticket, not in the form.
const HIDDEN_TYPES = new Set([
  "person",
  "checkbox",
  "file",
  "creator",
  "creator_department",
  "creator_company",
]);

function isReporterColumn(col: ColumnDef): boolean {
  return col.type === "person" && /^(reporter|gemeldet von)$/i.test(col.name.trim());
}

// The Ideenbox "Idee im Namen von" field is empty in ~99% of submissions — it's
// only filled when entering an idea for a colleague without a Flow account. A
// muted caption makes that obvious. Matched by name, like isReporterColumn.
function proxySubmitterHint(col: ColumnDef): string | null {
  return /^idee im namen von$/i.test(col.name.trim()) ? t.ideaProxyHint : null;
}

// The lifecycle status column carries semantic roles on its options; category
// status columns (Bereich, Priorität) carry none.
function isWorkflowStatusColumn(col: ColumnDef): boolean {
  return col.type === "status" && (col.config.options ?? []).some((o) => roleOf(o) !== null);
}

function isRequiredColumn(col: ColumnDef): boolean {
  return col.config.required === true;
}

// The person column a submitted idea assigns to — mirrors the backend
// (_assignment_person_column): prefer an explicitly-named responsibility column,
// else the first person column (Ideenbox has exactly one, "Gutachter"). Returns
// null when the board has no person column.
export function assignmentPersonColumn(columns: ColumnDef[]): ColumnDef | null {
  const persons = columns.filter((c) => c.type === "person");
  if (persons.length === 0) return null;
  const preferred = persons.find((c) =>
    ["verantwortlich", "zugewiesen an"].includes(c.name.trim().toLowerCase()),
  );
  return preferred ?? persons[0] ?? null;
}

type FormVariant = "public" | "internal";

export function visibleFormColumns(
  columns: ColumnDef[],
  variant: FormVariant,
  kind?: BoardKind,
): ColumnDef[] {
  // On an ideas board the submitter may optionally pick who the idea goes to, so
  // the one assignment person column is shown (all other person columns stay
  // hidden). On any other board, person columns remain hidden as before.
  const assignCol = kind === "ideas" ? assignmentPersonColumn(columns) : null;
  return columns.filter((c) => {
    if (isReporterColumn(c)) return true;
    if (c.type === "person") return assignCol != null && c.id === assignCol.id;
    if (c.type === "status") {
      // The workflow Status is always hidden (backend auto-defaults it to
      // "Offen"). For the public form, only a required category column (Bereich)
      // shows. For the internal manual-ticket form a department lead fills, show
      // every non-workflow status — including optional ones like Priorität.
      if (isWorkflowStatusColumn(c)) return false;
      return variant === "internal" || isRequiredColumn(c);
    }
    return !HIDDEN_TYPES.has(c.type);
  });
}

/**
 * The ticket-submission form body — fields + submit button + validation. Pure
 * presentation around the `/boards/{id}/submit` endpoint; the surrounding chrome
 * (page card vs modal) is the caller's concern. Shared by SubmitPage (standalone
 * `/submit/$boardId`) and SubmitTicketDialog (modal on Home).
 */
export function SubmitForm({
  boardId,
  detail,
  currentUserSub,
  onSubmitted,
  variant = "public",
  submitFn = api.submitToBoard,
  submitLabel,
  onDirtyChange,
}: {
  boardId: UUID;
  detail: BoardDetail;
  currentUserSub: string | null;
  onSubmitted: () => void;
  /** Reports whether the user has entered anything yet — lets a host dialog
   * confirm before discarding on accidental click-outside. */
  onDirtyChange?: (dirty: boolean) => void;
  /** "public" = cross-department /submit form; "internal" = the manual
   * "Neues Ticket" dialog a department lead fills (shows optional status
   * columns like Priorität too). */
  variant?: FormVariant;
  /** Endpoint the form posts to. Defaults to the public submit endpoint; the
   * manual-ticket dialog injects `api.createTicket` (member-gated). */
  submitFn?: (boardId: UUID, payload: { cells: Record<UUID, CellValue> }) => Promise<unknown>;
  submitLabel?: string;
}) {
  const columns = useMemo(
    () => (detail.columns ? visibleFormColumns(detail.columns, variant, detail.kind) : []),
    [detail, variant],
  );
  const reporterColumn = detail.columns?.find(isReporterColumn) ?? null;

  // Ideenbox: the optional "assign to a colleague" field + its caption telling
  // the submitter who gets the idea when they leave it empty (the configured
  // default assignee, resolved to a name from the board's people).
  const assignCol = detail.kind === "ideas" ? assignmentPersonColumn(detail.columns ?? []) : null;
  const boardPeople = useBoardPeople(assignCol ? boardId : undefined);
  const defaultAssigneeId = detail.view_config?.default_assignee_user_id ?? null;
  const assignHint = useMemo(() => {
    if (!assignCol) return undefined;
    if (!defaultAssigneeId) return t.ideaAssignHintNoDefault;
    const p = (boardPeople.data ?? []).find((x) => x.user_id === defaultAssigneeId);
    const name = p ? p.display_name || p.username || p.email : null;
    return name ? t.ideaAssignHint.replace("{name}", name) : t.ideaAssignHintNoDefault;
  }, [assignCol, defaultAssigneeId, boardPeople.data]);
  const firstTextColumn = columns.find((c) => c.type === "text") ?? null;
  // THE FALLBACK. A Datei column is optional: when the board has one, the
  // "Datei anhängen" button writes there; when it doesn't (the IT ticket board),
  // attachments keep going into the Beschreibung markdown exactly as before. No
  // board is forced to carry the column and nothing needs migrating.
  // `detail.columns`, not `columns` — file columns stay in HIDDEN_TYPES so the
  // form never grows a second, competing upload field.
  const fileColumn = detail.columns?.find((c) => c.type === "file") ?? null;

  const [values, setValues] = useState<Record<UUID, unknown>>({});
  const [errors, setErrors] = useState<Record<UUID, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Report "has the user typed anything" so a host dialog can guard against an
  // accidental click-outside discarding a half-filled ticket.
  useEffect(() => {
    if (!onDirtyChange) return;
    const dirty = Object.values(values).some(
      (v) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0),
    );
    onDirtyChange(dirty);
  }, [values, onDirtyChange]);

  function setVal(colId: UUID, v: unknown) {
    setValues((prev) => ({ ...prev, [colId]: v }));
    setErrors((prev) => {
      if (!(colId in prev)) return prev;
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const errs: Record<UUID, string> = {};
    const payload: Record<UUID, CellValue> = {};

    if (firstTextColumn) {
      const raw = values[firstTextColumn.id];
      if (typeof raw !== "string" || raw.trim() === "") {
        errs[firstTextColumn.id] = t.submitRequiredField;
      }
    }

    // Required single-selects (e.g. Bereich) must be picked. The encode loop
    // below skips empty values, so this is what actually blocks submission.
    for (const col of columns) {
      if (!isRequiredColumn(col)) continue;
      const raw = values[col.id];
      if (raw === undefined || raw === null || raw === "") {
        errs[col.id] = t.submitRequiredField;
      }
    }

    for (const col of columns) {
      const raw = values[col.id];
      if (raw === undefined || raw === null || raw === "") continue;
      try {
        payload[col.id] = encodeForApi(col, raw);
      } catch (err) {
        errs[col.id] = err instanceof Error ? err.message : "Ungültiger Wert";
      }
    }

    // Auto-fill the Reporter person column with the current Keycloak sub; the
    // backend looks the user up by sub.
    if (reporterColumn && currentUserSub) {
      payload[reporterColumn.id] = [currentUserSub] as CellValue;
    }

    // The Datei column is HIDDEN, so the loop above (which walks only visible
    // columns) never sees it — its refs have to be attached by hand or the
    // upload silently vanishes on submit.
    if (fileColumn) {
      const refs = values[fileColumn.id];
      if (Array.isArray(refs) && refs.length > 0) payload[fileColumn.id] = refs as FileRef[];
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await submitFn(boardId, { cells: payload });
      onSubmitted();
    } catch (err) {
      const detailMsg =
        err instanceof ApiError && typeof err.detail === "object" && err.detail
          ? JSON.stringify(err.detail)
          : err instanceof Error
            ? err.message
            : t.unknownError;
      showToast(detailMsg, "error", 4000);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {detail.columns && columns.length === 0 ? <NoFieldsNotice /> : null}
      <form onSubmit={onSubmit}>
        {columns.map((col) => (
          <FormField
            key={col.id}
            column={col}
            value={values[col.id]}
            onChange={(v) => setVal(col.id, v)}
            error={errors[col.id]}
            required={col.id === firstTextColumn?.id || isRequiredColumn(col)}
            boardId={boardId}
            {...(assignCol && col.id === assignCol.id && assignHint
              ? { extraHint: assignHint }
              : {})}
            {...(fileColumn && col.type === "long_text"
              ? { onAttachments: (refs: FileRef[]) => setVal(fileColumn.id, refs) }
              : {})}
          />
        ))}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button type="submit" variant="primary" size="md" icon="check" disabled={submitting}>
            {submitting ? t.submitting : (submitLabel ?? t.submitButton)}
          </Button>
        </div>
      </form>
    </>
  );
}

function NoFieldsNotice() {
  return (
    <div
      style={{
        padding: "12px 14px",
        marginBottom: 14,
        borderRadius: 8,
        background: "var(--surface-soft)",
        border: "1px dashed var(--hairline-strong)",
        fontSize: 12.5,
        color: "var(--ink-3)",
      }}
    >
      {t.submitNoFields}
    </div>
  );
}

export function FormField({
  column,
  value,
  onChange,
  error,
  required,
  boardId,
  onAttachments,
  extraHint,
}: {
  column: ColumnDef;
  value: unknown;
  onChange: (v: unknown) => void;
  error: string | undefined;
  required: boolean;
  /** Board context — enables the inline person picker (add-row modal). */
  boardId?: UUID;
  /** Present only when the board has a `file` column: the long-text field's
   * staged attachments are written there instead of into the prose markdown. */
  onAttachments?: (refs: FileRef[]) => void;
  /** Caller-supplied caption under the field (e.g. the Ideenbox assignment
   * field's "ohne Auswahl geht die Idee an …"). A column's own built-in hint
   * (proxySubmitterHint) takes precedence. */
  extraHint?: string;
}) {
  const id = useId();
  const fieldHint = proxySubmitterHint(column) ?? extraHint ?? null;
  // Checkbox renders its own inline label+control (no separate caption row).
  if (column.type === "checkbox") {
    return (
      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            ...labelStyle,
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: BRAND.blue, cursor: "pointer" }}
          />
          {column.name}
          {required ? <span style={{ color: "var(--danger)", marginLeft: 4 }}>*</span> : null}
        </label>
        {error ? <div style={errorStyle}>{error}</div> : null}
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={labelStyle}>
        {column.name}
        {required ? <span style={{ color: "var(--danger)", marginLeft: 4 }}>*</span> : null}
      </label>
      {column.type === "person" ? (
        <PersonField boardId={boardId} value={value} onChange={onChange} />
      ) : (
        renderControl(column, id, value, onChange, onAttachments)
      )}
      {fieldHint ? <div style={hintStyle}>{fieldHint}</div> : null}
      {error ? <div style={errorStyle}>{error}</div> : null}
    </div>
  );
}

// Inline (non-portaled) person multi-select for the add-row modal — a portaled
// PersonPicker inside the modal's focus trap eats keystrokes, so this renders the
// search + results in normal flow (the focus-trap lesson from the share dialog).
function PersonField({
  boardId,
  value,
  onChange,
}: {
  boardId: UUID | undefined;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const [q, setQ] = useState("");
  const people = useBoardPeople(boardId);
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const all = people.data ?? [];
  const nameOf = (uid: string) => {
    const p = all.find((x) => x.user_id === uid);
    return p?.display_name || p?.username || p?.email || uid;
  };
  const needle = q.trim().toLowerCase();
  const matches = all
    .filter((p) => !selected.includes(p.user_id))
    .filter((p) =>
      needle
        ? `${p.display_name ?? ""} ${p.username ?? ""} ${p.email}`.toLowerCase().includes(needle)
        : true,
    )
    .slice(0, 6);
  const toggle = (uid: string) =>
    onChange(selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {selected.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selected.map((uid) => (
            <button
              key={uid}
              type="button"
              onClick={() => toggle(uid)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px solid var(--hairline-strong)",
                background: "var(--surface-elev)",
                fontSize: 12.5,
                color: "var(--ink-1)",
                cursor: "pointer",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              {nameOf(uid)} ×
            </button>
          ))}
        </div>
      ) : null}
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t.personSearchPlaceholder}
        style={inputStyle}
      />
      {needle && matches.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {matches.map((p) => (
            <button
              key={p.user_id}
              type="button"
              onClick={() => {
                toggle(p.user_id);
                setQ("");
              }}
              style={{
                textAlign: "left",
                padding: "7px 10px",
                background: "transparent",
                border: "none",
                borderBottom: "1px solid var(--hairline-soft)",
                cursor: "pointer",
                fontSize: 13,
                color: "var(--ink-1)",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {p.display_name || p.username || p.email}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function renderControl(
  column: ColumnDef,
  id: string,
  value: unknown,
  onChange: (v: unknown) => void,
  onAttachments?: (refs: FileRef[]) => void,
): React.ReactNode {
  switch (column.type) {
    case "text":
      return (
        <input
          id={id}
          type="text"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          maxLength={(column.config.max_length as number) ?? 1024}
          style={inputStyle}
        />
      );
    case "long_text":
      return (
        <LongTextField
          value={(value as string) ?? ""}
          onChange={(v) => onChange(v)}
          placeholder={t.formLongTextHint}
          {...(onAttachments ? { onAttachments } : {})}
        />
      );
    case "status": {
      const opts = (
        (column.config.options as Array<{
          id: string;
          label: string;
          color?: string;
          archived?: boolean;
        }>) ?? []
      ).filter((o) => !o.archived);
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {opts.map((o) => {
            const active = value === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => onChange(active ? "" : o.id)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: `1px solid ${active ? BRAND.blue : "var(--hairline-strong)"}`,
                  background: active ? "var(--surface-elev)" : "var(--surface)",
                  color: "var(--ink-1)",
                  fontWeight: active ? 600 : 500,
                  fontSize: 12.5,
                  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                  cursor: "pointer",
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case "date":
      return (
        <input
          id={id}
          type="date"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case "number": {
      const cfg = column.config;
      return (
        <input
          id={id}
          type="number"
          value={(value as string | number | undefined) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          min={cfg.min as number | undefined}
          max={cfg.max as number | undefined}
          step={cfg.decimals ? Math.pow(10, -(cfg.decimals as number)) : "any"}
          style={{
            ...inputStyle,
            fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
          }}
        />
      );
    }
    case "tags":
      return (
        <input
          id={id}
          type="text"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t.submitTagsPlaceholder}
          style={inputStyle}
        />
      );
    case "link": {
      const v = (value as { url?: string; label?: string }) ?? {};
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            id={id}
            type="url"
            value={v.url ?? ""}
            onChange={(e) => onChange({ ...v, url: e.target.value })}
            placeholder={t.submitLinkUrlPlaceholder}
            style={inputStyle}
          />
          <input
            type="text"
            value={v.label ?? ""}
            onChange={(e) => onChange({ ...v, label: e.target.value })}
            placeholder={t.submitLinkLabelPlaceholder}
            style={inputStyle}
          />
        </div>
      );
    }
    default:
      return null;
  }
}

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ink-2)",
  marginBottom: 5,
};

const inputStyle: CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  fontSize: 14,
  borderRadius: 8,
  border: "1px solid var(--hairline-strong)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  outline: "none",
};

const errorStyle: CSSProperties = {
  fontSize: 11.5,
  color: "var(--danger)",
  marginTop: 5,
};

const hintStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--ink-4)",
  marginTop: 4,
  lineHeight: 1.4,
};
